import { Periodicidade, periodicidadeAnual, periodicidadeDiaria, periodicidadeMensal, periodicidadeSemestral, periodicidadeTrimestral } from '../../../../../shared/periodicidade';
import { getDateFromSemestre, getDateFromTrimestre, getSemestreFromDate, getTrimestreFromDate } from '../novo-datepicker.utils';

const stepCalculators: Record<number, (d: Date, dir: 1 | -1) => Date> = {
  [periodicidadeDiaria.id]: (d, dir) => {
    const r = new Date(d);
    r.setDate(r.getDate() + dir);
    return r;
  },
  [periodicidadeMensal.id]: (d, dir) => {
    const r = new Date(d);
    r.setDate(1);
    r.setMonth(r.getMonth() + dir);
    return r;
  },
  [periodicidadeTrimestral.id]: (d, dir) => {
    const q = getTrimestreFromDate(d);
    const next = q + dir;
    if (next < 1) {
      return getDateFromTrimestre(4, d.getFullYear() - 1);
    }
    if (next > 4) {
      return getDateFromTrimestre(1, d.getFullYear() + 1);
    }
    return getDateFromTrimestre(next, d.getFullYear());
  },
  [periodicidadeSemestral.id]: (d, dir) => {
    const s = getSemestreFromDate(d);
    const next = s + dir;
    if (next < 1) {
      return getDateFromSemestre(2, d.getFullYear() - 1);
    }
    if (next > 2) {
      return getDateFromSemestre(1, d.getFullYear() + 1);
    }
    return getDateFromSemestre(next, d.getFullYear());
  },
  [periodicidadeAnual.id]: (d, dir) => new Date(d.getFullYear() + dir, 0, 1)
};

const stepTitles: Record<number, { prev: string; next: string }> = {
  [periodicidadeDiaria.id]: { prev: 'Dia Anterior', next: 'Próximo Dia' },
  [periodicidadeMensal.id]: { prev: 'Mês Anterior', next: 'Próximo Mês' },
  [periodicidadeTrimestral.id]: {
    prev: 'Trimestre Anterior',
    next: 'Próximo Trimestre'
  },
  [periodicidadeSemestral.id]: {
    prev: 'Semestre Anterior',
    next: 'Próximo Semestre'
  },
  [periodicidadeAnual.id]: { prev: 'Ano Anterior', next: 'Próximo Ano' }
};

/**
 * Calcula a próxima/anterior data conforme a periodicidade.
 *
 * Retorna null para periodicidades sem navegação por passo (ex: Período,
 * que opera sobre range de datas). Callers devem tratar null como "operação
 * indisponível", não como erro.
 *
 * Para periodicidades que normalizam o resultado para dia 1
 * (mensal/trimestral/semestral/anual), a normalização é defensiva contra
 * writeValue externo com dia > 28 — no fluxo normal de UI, o valor já
 * chega normalizado pelos handlers de seleção.
 */
export function computeStep(
  date: Date,
  periodicidade: Periodicidade,
  direction: 1 | -1
): Date | null {
  const calc = stepCalculators[periodicidade.id];
  return calc ? calc(date, direction) : null;
}

/**
 * Devolve o tooltip do botão prev/next para a periodicidade.
 *
 * Retorna string vazia para periodicidade sem entrada — evita botão com
 * tooltip quebrado ou inconsistente quando a navegação não se aplica.
 */
export function getStepTitle(
  periodicidade: Periodicidade,
  direction: 1 | -1
): string {
  const titles = stepTitles[periodicidade.id];
  if (!titles) {
    return '';
  }
  return direction === -1 ? titles.prev : titles.next;
}
